import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertTriangle } from 'lucide-react';
import { formatBookingReference } from '../../../utils/bookingUtils';
import { formatTimeRange } from '../BookingSeries/seriesHelpers';

export default function ConfirmReleaseDialog({ booking, open, onOpenChange, releasing, onConfirm }) {
  const [reason, setReason] = useState('');

  // Reset the reason field each time the dialog is opened for a (possibly
  // different) booking, so stale text never carries over to the next release.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !releasing && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-release-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Release this booking?
          </DialogTitle>
          <DialogDescription>
            This frees the court slot immediately so it becomes bookable by the public again.
            The payment stays on file — this does NOT touch payment records or issue a refund.
            The customer will be notified by email.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
          <div className="font-mono text-xs text-muted-foreground">
            {formatBookingReference(booking.id)}
          </div>
          <div className="font-medium mt-1">{booking.booking_date} · {formatTimeRange(booking.time_slot, booking.duration)}</div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="release-reason-input" className="text-xs uppercase font-bold text-muted-foreground">
            Reason (optional)
          </Label>
          <Textarea
            id="release-reason-input"
            placeholder="e.g. Court needed for a tournament — customer rebooked for next week"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={releasing}
            rows={3}
            data-testid="release-reason-input"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={releasing}
            data-testid="confirm-release-cancel-btn"
          >
            Keep Booking
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(reason)}
            disabled={releasing}
            data-testid="confirm-release-submit-btn"
          >
            {releasing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Releasing…</>
            ) : 'Release Booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
